/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { Typography2 } from '@coursera/cds-core';

import SparkleIcon from 'bundles/unified-common/components/coach-on-discovery/SparkleIcon';
import {
  WES_ASK_COURSERA_HEADER_SLOT_ID,
  WES_CHATBOT_DIALOG_ID,
} from 'bundles/wes-common/components/WESAskCoursera/constants';

import _t from 'i18n!nls/wes-common';

const styles = {
  headerButton: css`
    display: flex;
    align-items: center;
    min-width: 136px;
    height: 36px;
    gap: var(--cds-spacing-50);
    margin: 0 var(--cds-spacing-100) 0 var(--cds-spacing-100);
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    white-space: nowrap;
    border-radius: var(--cds-border-radius-100);
    border: 1px solid var(--cds-color-interactive-primary);
    background-color: var(--cds-color-white-0);
    box-shadow: none;
    cursor: pointer;
    font: inherit;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--cds-color-emphasis-primary-background-xweak);
    }

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 2px;
    }

    &:active {
      filter: brightness(0.95);
    }

    path {
      fill: var(--cds-color-interactive-primary);
    }
  `,

  headerButtonText: css`
    letter-spacing: 0.01em;
    text-align: center;
    white-space: nowrap;
    color: var(--cds-color-interactive-primary);
  `,
};

type HeaderLauncherButtonProps = {
  isOpen: boolean;
  onClick: () => void;
};

const WESAskCourseraHeaderLauncher = ({ isOpen, onClick }: HeaderLauncherButtonProps) => {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setPortalTarget(document.getElementById(WES_ASK_COURSERA_HEADER_SLOT_ID));
  }, []);

  const button = (
    <button
      type="button"
      css={styles.headerButton}
      onClick={onClick}
      aria-label={isOpen ? _t('Close AI Chatbot') : _t('Open AI Chatbot')}
      aria-expanded={isOpen}
      aria-haspopup="dialog"
      aria-controls={isOpen ? WES_CHATBOT_DIALOG_ID : undefined}
      data-testid="WESAskCourseraLauncher-header"
    >
      <SparkleIcon size="small" />
      <Typography2 variant="subtitleSmall" color="emphasis.primary" component="span" css={styles.headerButtonText}>
        {_t('Ask Coursera')}
      </Typography2>
    </button>
  );

  if (!portalTarget) {
    return null;
  }

  return createPortal(<div data-testid="WESAskCourseraLauncher">{button}</div>, portalTarget);
};

export default WESAskCourseraHeaderLauncher;
